import { observer } from "mobx-react-lite";
import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { usePlayground } from "../playground-store";
import { PLAYGROUND_TOUR_STEP_INDICES } from "../tour/constants";
import { useOptionalPlaygroundTourContext } from "../tour/context";
import { PLAYGROUND_DRAWER_MODES, PLAYGROUND_SESSION_MODES } from "../types";

import ControlBar from "./ControlBar";
import DrawerContent from "./DrawerContent";

import { cn } from "@/lib/utils";

interface DrawerHeightProfile {
  defaultHeight: number;
  minHeight: number;
  maxHeightRatio: number;
}

const OUTPUT_DRAWER_HEIGHT: DrawerHeightProfile = {
  defaultHeight: 220,
  minHeight: 220,
  maxHeightRatio: 0.82,
};

const CHAT_DRAWER_HEIGHT: DrawerHeightProfile = {
  defaultHeight: 360,
  minHeight: 360,
  maxHeightRatio: 0.9,
};

const TOUR_OUTPUT_RESULTS_HEIGHT = 440;
const TOUR_WORKFLOW_HEIGHT = 380;

const DRAWER_PEEK_HEIGHT = 42;
const DRAWER_COLLAPSE_THRESHOLD = 120;
const DRAWER_CONTENT_BOTTOM_PADDING = 8;
const PROVIDER_API_KEY_PANEL_SELECTOR = "[data-provider-api-key-panel]";
const OUTPUTS_AUTO_FIT_CONTENT_SELECTOR = "[data-playground-outputs-auto-fit-content]";

type FitContentTarget = "provider-api-key" | "outputs";

const getMaxDrawerHeight = (profile: DrawerHeightProfile) => {
  if (typeof window === "undefined") {
    return 760;
  }

  return Math.max(profile.minHeight, Math.floor(window.innerHeight * profile.maxHeightRatio));
};

const clampExpandedDrawerHeight = (height: number, profile: DrawerHeightProfile) =>
  Math.min(Math.max(height, profile.minHeight), getMaxDrawerHeight(profile));

const clampDrawerHeight = (height: number, profile: DrawerHeightProfile) => {
  if (height <= DRAWER_COLLAPSE_THRESHOLD) {
    return DRAWER_PEEK_HEIGHT;
  }

  return clampExpandedDrawerHeight(height, profile);
};

const Drawer = () => {
  const playground = usePlayground();
  const tour = useOptionalPlaygroundTourContext();
  const isChatDrawer =
    playground.isChat && playground.sessionMode === PLAYGROUND_SESSION_MODES.CHAT;
  const isEvaluateDrawer =
    !isChatDrawer && playground.drawerMode === PLAYGROUND_DRAWER_MODES.EVALUATE;
  const heightProfile = isChatDrawer ? CHAT_DRAWER_HEIGHT : OUTPUT_DRAWER_HEIGHT;
  const drawerMode = isChatDrawer ? "chat" : playground.drawerMode;
  const previousDrawerModeRef = useRef(drawerMode);
  const initialHeight = clampExpandedDrawerHeight(heightProfile.defaultHeight, heightProfile);
  const [height, setHeight] = useState(initialHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [fitContentHeight, setFitContentHeight] = useState<number | null>(null);
  const [fitContentTarget, setFitContentTarget] = useState<FitContentTarget | null>(null);
  const [isOutputAutoFitEnabled, setIsOutputAutoFitEnabled] = useState(false);
  const [hasUserAdjustedOutputDrawer, setHasUserAdjustedOutputDrawer] = useState(false);
  const [hasAppliedInitialEvaluateFit, setHasAppliedInitialEvaluateFit] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastExpandedHeightRef = useRef(initialHeight);
  const previousRunningOperationCountRef = useRef(0);
  const dragStartRef = useRef<{ y: number; height: number; hasMoved: boolean } | null>(null);
  const isTourActive = tour?.isActive ?? false;
  const isTourWorkflowStep =
    tour !== null &&
    tour.currentStep >= PLAYGROUND_TOUR_STEP_INDICES.OUTPUT_WORKFLOW &&
    tour.currentStep <= PLAYGROUND_TOUR_STEP_INDICES.CHAT_WORKFLOW;
  let tourHeight = OUTPUT_DRAWER_HEIGHT.defaultHeight;
  if (tour?.currentStep === PLAYGROUND_TOUR_STEP_INDICES.OUTPUT_RESULTS) {
    tourHeight = TOUR_OUTPUT_RESULTS_HEIGHT;
  } else if (isTourWorkflowStep) {
    tourHeight = TOUR_WORKFLOW_HEIGHT;
  }
  const displayedHeight = isTourActive
    ? clampExpandedDrawerHeight(tourHeight, heightProfile)
    : height;
  const isCollapsed = !isTourActive && height === DRAWER_PEEK_HEIGHT;
  const runningOperationCount = playground.operationStore.ongoingOperations;
  const shouldAutoFitOutputs =
    !isChatDrawer &&
    !hasUserAdjustedOutputDrawer &&
    (isEvaluateDrawer ||
      isOutputAutoFitEnabled ||
      playground.hasOutputContent ||
      playground.runGroupError !== null);
  const fitDrawerHeight = useMemo(() => {
    if (fitContentHeight === null) {
      return null;
    }

    return (headerRef.current?.offsetHeight ?? 0) + fitContentHeight;
  }, [fitContentHeight]);
  const effectiveHeightProfile = useMemo(() => {
    if (
      fitDrawerHeight === null ||
      hasUserAdjustedOutputDrawer ||
      (isEvaluateDrawer && hasAppliedInitialEvaluateFit)
    ) {
      return heightProfile;
    }

    const fitHeight = Math.min(fitDrawerHeight, getMaxDrawerHeight(heightProfile));

    return {
      ...heightProfile,
      defaultHeight: Math.max(heightProfile.defaultHeight, fitHeight),
      minHeight: Math.max(heightProfile.minHeight, fitHeight),
    };
  }, [
    fitDrawerHeight,
    hasAppliedInitialEvaluateFit,
    hasUserAdjustedOutputDrawer,
    heightProfile,
    isEvaluateDrawer,
  ]);

  const measureFitContent = useCallback(() => {
    const content = contentRef.current;
    if (!content) {
      setFitContentHeight(null);
      setFitContentTarget(null);
      return;
    }

    const providerApiKeyPanel = hasUserAdjustedOutputDrawer
      ? null
      : content.querySelector<HTMLElement>(PROVIDER_API_KEY_PANEL_SELECTOR);
    const outputsAutoFitContent = shouldAutoFitOutputs
      ? content.querySelector<HTMLElement>(OUTPUTS_AUTO_FIT_CONTENT_SELECTOR)
      : null;
    const fitTarget = providerApiKeyPanel ?? outputsAutoFitContent;

    if (!fitTarget) {
      if (shouldAutoFitOutputs && fitContentTarget === "outputs") {
        return;
      }

      setFitContentHeight(null);
      setFitContentTarget(null);
      return;
    }

    if (fitTarget.dataset.playgroundOutputsAutoFitReady === "false") {
      setFitContentHeight(null);
      setFitContentTarget(null);
      return;
    }

    const contentRect = content.getBoundingClientRect();
    const targetRect = fitTarget.getBoundingClientRect();
    const nextFitContentHeight = Math.ceil(
      targetRect.bottom - contentRect.top + DRAWER_CONTENT_BOTTOM_PADDING,
    );
    const nextFitContentTarget: FitContentTarget = providerApiKeyPanel
      ? "provider-api-key"
      : "outputs";

    setFitContentHeight(currentHeight =>
      currentHeight === nextFitContentHeight ? currentHeight : nextFitContentHeight,
    );
    setFitContentTarget(currentTarget =>
      currentTarget === nextFitContentTarget ? currentTarget : nextFitContentTarget,
    );
  }, [fitContentTarget, hasUserAdjustedOutputDrawer, shouldAutoFitOutputs]);

  useEffect(() => {
    if (isCollapsed) {
      setFitContentHeight(null);
      setFitContentTarget(null);
      return;
    }

    const content = contentRef.current;
    if (!content) {
      return;
    }

    let animationFrame: number | null = null;
    const scheduleMeasure = () => {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(measureFitContent);
    };

    scheduleMeasure();

    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeFilter: [
        "class",
        "style",
        "data-provider-api-key-panel",
        "data-playground-outputs-auto-fit-content",
        "data-playground-outputs-auto-fit-ready",
      ],
    });

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    resizeObserver.observe(content);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isCollapsed, measureFitContent]);

  useEffect(() => {
    const previousRunningOperationCount = previousRunningOperationCountRef.current;

    if (!isChatDrawer && runningOperationCount > previousRunningOperationCount) {
      setIsOutputAutoFitEnabled(true);
      setHeight(currentHeight => {
        if (currentHeight !== DRAWER_PEEK_HEIGHT) {
          return currentHeight;
        }

        return clampExpandedDrawerHeight(
          effectiveHeightProfile.defaultHeight,
          effectiveHeightProfile,
        );
      });
    }

    let completionFrame: number | null = null;
    if (
      !isChatDrawer &&
      isOutputAutoFitEnabled &&
      previousRunningOperationCount > 0 &&
      runningOperationCount === 0
    ) {
      completionFrame = requestAnimationFrame(() => {
        setIsOutputAutoFitEnabled(false);
      });
    }

    previousRunningOperationCountRef.current = runningOperationCount;

    return () => {
      if (completionFrame !== null) {
        cancelAnimationFrame(completionFrame);
      }
    };
  }, [effectiveHeightProfile, isChatDrawer, isOutputAutoFitEnabled, runningOperationCount]);

  useEffect(() => {
    const handleResize = () => {
      setHeight(currentHeight => {
        if (currentHeight === DRAWER_PEEK_HEIGHT) {
          return DRAWER_PEEK_HEIGHT;
        }

        return clampExpandedDrawerHeight(currentHeight, effectiveHeightProfile);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [effectiveHeightProfile]);

  useEffect(() => {
    if (previousDrawerModeRef.current !== drawerMode) {
      previousDrawerModeRef.current = drawerMode;
      setIsOutputAutoFitEnabled(false);
      setHasUserAdjustedOutputDrawer(false);
      setHasAppliedInitialEvaluateFit(false);
      setFitContentHeight(null);
      setFitContentTarget(null);
      const nextHeight = clampExpandedDrawerHeight(heightProfile.defaultHeight, heightProfile);
      lastExpandedHeightRef.current = nextHeight;
      setHeight(nextHeight);
      return;
    }

    const shouldFollowOutputContent =
      shouldAutoFitOutputs &&
      fitContentTarget === "outputs" &&
      fitDrawerHeight !== null &&
      (!isEvaluateDrawer || !hasAppliedInitialEvaluateFit);

    setHeight(currentHeight => {
      const nextFitHeight =
        fitDrawerHeight === null
          ? null
          : clampExpandedDrawerHeight(fitDrawerHeight, effectiveHeightProfile);

      if (currentHeight === DRAWER_PEEK_HEIGHT) {
        return nextFitHeight ?? DRAWER_PEEK_HEIGHT;
      }

      if (shouldFollowOutputContent && nextFitHeight !== null) {
        return nextFitHeight;
      }

      return clampExpandedDrawerHeight(currentHeight, effectiveHeightProfile);
    });

    if (isEvaluateDrawer && shouldFollowOutputContent) {
      setHasAppliedInitialEvaluateFit(true);
    }
  }, [
    drawerMode,
    effectiveHeightProfile,
    fitContentTarget,
    fitDrawerHeight,
    hasAppliedInitialEvaluateFit,
    heightProfile,
    isEvaluateDrawer,
    shouldAutoFitOutputs,
  ]);

  useEffect(() => {
    if (!isCollapsed) {
      lastExpandedHeightRef.current = height;
    }
  }, [height, isCollapsed]);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const dragStart = dragStartRef.current;
      if (!dragStart) {
        return;
      }

      if (Math.abs(event.clientY - dragStart.y) > 4) {
        dragStart.hasMoved = true;
      }

      const nextHeight = dragStart.height + dragStart.y - event.clientY;
      setHeight(clampDrawerHeight(nextHeight, heightProfile));
    },
    [heightProfile],
  );

  const handlePointerUp = useCallback(() => {
    const dragStart = dragStartRef.current;

    if (dragStart && !dragStart.hasMoved) {
      setHeight(currentHeight =>
        currentHeight === DRAWER_PEEK_HEIGHT
          ? clampExpandedDrawerHeight(lastExpandedHeightRef.current, heightProfile)
          : DRAWER_PEEK_HEIGHT,
      );
    }

    dragStartRef.current = null;
    setIsDragging(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
  }, [handlePointerMove, heightProfile]);

  useEffect(
    () => () => {
      setIsDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    },
    [handlePointerMove, handlePointerUp],
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOutputAutoFitEnabled(false);
    setHasUserAdjustedOutputDrawer(true);
    setFitContentHeight(null);
    setFitContentTarget(null);
    dragStartRef.current = { y: event.clientY, height, hasMoved: false };
    setIsDragging(true);
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  const expandDrawer = useCallback(() => {
    if (!isCollapsed) {
      return;
    }

    setIsOutputAutoFitEnabled(true);
    setHasUserAdjustedOutputDrawer(false);
    setFitContentHeight(null);
    setFitContentTarget(null);
    setHeight(clampExpandedDrawerHeight(lastExpandedHeightRef.current, effectiveHeightProfile));
  }, [effectiveHeightProfile, isCollapsed]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOutputAutoFitEnabled(false);
      setHasUserAdjustedOutputDrawer(true);
      setFitContentHeight(null);
      setFitContentTarget(null);
      setHeight(currentHeight =>
        currentHeight === DRAWER_PEEK_HEIGHT
          ? clampExpandedDrawerHeight(lastExpandedHeightRef.current, heightProfile)
          : DRAWER_PEEK_HEIGHT,
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOutputAutoFitEnabled(false);
      setHasUserAdjustedOutputDrawer(true);
      setFitContentHeight(null);
      setFitContentTarget(null);
      setHeight(currentHeight =>
        currentHeight === DRAWER_PEEK_HEIGHT
          ? clampExpandedDrawerHeight(lastExpandedHeightRef.current, heightProfile)
          : clampDrawerHeight(currentHeight + 24, heightProfile),
      );
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOutputAutoFitEnabled(false);
      setHasUserAdjustedOutputDrawer(true);
      setFitContentHeight(null);
      setFitContentTarget(null);
      setHeight(currentHeight => {
        if (currentHeight <= heightProfile.minHeight) {
          return DRAWER_PEEK_HEIGHT;
        }

        return clampDrawerHeight(currentHeight - 24, heightProfile);
      });
    }
  };

  return (
    <div className="relative z-10 w-full shrink-0 overflow-visible">
      <aside
        className={cn(
          "relative flex w-full flex-col overflow-hidden rounded-t-[18px] border border-b-0 border-gray-200 bg-white",
          "shadow-[0_-9px_20px_-16px_rgba(15,23,42,0.20)]",
          "transition-[height] duration-200 ease-out",
          (isDragging || isTourActive) && "transition-none",
          isCollapsed && "shadow-[0_-5px_12px_-12px_rgba(15,23,42,0.14)]",
        )}
        style={{ height: displayedHeight }}
        aria-label="Playground controls and output drawer"
      >
        <header
          ref={headerRef}
          className={cn(
            "relative shrink-0 bg-white/95 backdrop-blur",
            isCollapsed ? "h-full border-b-0" : "border-b border-gray-100",
          )}
        >
          <div
            className={cn(
              "group absolute left-1/2 top-1 z-10 flex h-5 w-16 -translate-x-1/2 cursor-ns-resize items-center justify-center rounded-md transition-colors duration-200",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/40",
              isCollapsed && "top-0 h-4",
            )}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize controls and output drawer"
            aria-valuemin={DRAWER_PEEK_HEIGHT}
            aria-valuemax={getMaxDrawerHeight(effectiveHeightProfile)}
            aria-valuenow={displayedHeight}
            aria-valuetext={isCollapsed ? "Collapsed" : `${displayedHeight}px`}
            tabIndex={0}
          >
            <span className="h-1 w-10 rounded-full bg-gray-300 transition-colors duration-200 group-hover:bg-gray-400" />
          </div>
          <ControlBar collapsed={isCollapsed} onRequestExpandForRun={expandDrawer} />
        </header>

        {!isCollapsed && (
          <div ref={contentRef} className="min-h-0 flex-1 overflow-hidden bg-gray-50/60 pt-2">
            <DrawerContent />
          </div>
        )}
      </aside>
    </div>
  );
};

export default observer(Drawer);
